/**
 * Detect and split URLs in chat message text. No HTML injection.
 */

const URL_RE =
  /(?:https?:\/\/|www\.)[^\s<>'"()\[\]{}]+|(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:com|org|net|edu|gov|io|co|in|uk|us|info|biz|me|app|dev|ai|tv|xyz)(?:\/[^\s<>'"()\[\]{}]*)?/gi;

function trimTrailingPunctuation(url) {
  return url.replace(/[.,;:!?)]+$/g, '');
}

export function normalizeHref(raw) {
  const trimmed = trimTrailingPunctuation((raw || '').trim());
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (/^www\./i.test(trimmed) || /^[a-z0-9.-]+\.[a-z]{2,}/i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return null;
}

export function extractUrls(text) {
  if (!text) return [];
  const found = [];
  const seen = new Set();
  const matches = String(text).match(URL_RE) || [];
  for (const m of matches) {
    const href = normalizeHref(m);
    if (href && !seen.has(href)) {
      seen.add(href);
      found.push(href);
    }
  }
  return found;
}

export function firstUrlInBody(text) {
  const urls = extractUrls(text);
  return urls[0] || null;
}

/**
 * @returns {Array<{ type: 'text'|'link', value: string, href?: string }>}
 */
export function splitTextWithLinks(text) {
  if (!text) return [];
  const str = String(text);
  const parts = [];
  let lastIndex = 0;
  const re = new RegExp(URL_RE.source, 'gi');
  let match;
  while ((match = re.exec(str)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: str.slice(lastIndex, match.index) });
    }
    const raw = match[0];
    const cleaned = trimTrailingPunctuation(raw);
    const trailing = raw.slice(cleaned.length);
    const href = normalizeHref(cleaned);
    if (href) {
      parts.push({ type: 'link', value: cleaned, href });
      if (trailing) parts.push({ type: 'text', value: trailing });
    } else {
      parts.push({ type: 'text', value: raw });
    }
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < str.length) {
    parts.push({ type: 'text', value: str.slice(lastIndex) });
  }
  return parts.length ? parts : [{ type: 'text', value: str }];
}
